import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { RecruiterSkeleton, ErrorState, EmptyState } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Briefcase, Plus, Search, Library, MoreHorizontal } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { CandidateInvitations } from "@/components/CandidateInvitations";
import { LifecycleBar, NextStep, lifecycleCaption, sectionPath } from "@/components/job/Lifecycle";

const FILTERS = [
  ["all", "All", () => true],
  ["attention", "Needs attention", (j) => ["amber", "blue"].includes(j.lifecycle?.next?.tone)],
  ["drafts", "Drafts", (j) => j.status === "draft"],
  ["live", "Live", (j) => j.status === "published"],
  ["closed", "Closed", (j) => ["closed", "archived"].includes(j.status)],
];

function UsageWidget({ onSeats }) {
  const [u, setU] = useState(null);
  const [seats, setSeats] = useState(null);
  useEffect(() => {
    api.get("/ai/usage").then((r) => setU(r.data)).catch(() => {});
    api.get("/assessment-seats").then((r) => setSeats(r.data)).catch(() => {});
  }, []);
  if (!u && !seats) return null;
  const pct = u?.monthly_ai_budget ? Math.min(100, (u.ai_spend / u.monthly_ai_budget) * 100) : 0;
  return (
    <div className="hs-card hidden md:flex items-center divide-x divide-border text-[12px]" data-testid="jobs-usage">
      {u && <div className="px-4 py-2.5 space-y-1.5 min-w-[180px]" data-testid="ai-budget-widget">
        <div className="flex items-baseline gap-2 text-muted-foreground">AI budget <span className="hs-mono text-foreground font-medium">{u.ai_spend.toLocaleString()} / {u.monthly_ai_budget.toLocaleString()}</span></div>
        <div className="h-1 rounded-full bg-foreground/[0.08]"><div className={cn("h-full rounded-full", pct > 85 ? "bg-amber-400" : "bg-primary")} style={{ width: `${pct}%` }} /></div>
      </div>}
      {seats && <button onClick={onSeats} className="px-4 py-2.5 text-left hover:bg-foreground/[0.03] transition-colors" data-testid="invite-seats-widget">
        <div className="text-muted-foreground">Invite seats</div>
        <div className="hs-mono font-medium">{seats.length} · {seats.filter((s) => s.claimed).length} used</div>
      </button>}
    </div>
  );
}

function JobRow({ j, onDelete }) {
  const nav = useNavigate();
  const lc = j.lifecycle || { step: 1 };
  const live = j.status === "published";
  const open = () => nav(sectionPath(j.id, lc.next?.section || "build"));
  const cands = live ? `${lc.total || 0}${lc.to_review ? ` · ${lc.to_review} new` : lc.counts?.interview ? ` · ${lc.counts.interview} in interview` : ""}` : "Not live yet";
  const meta = [j.location, j.work_mode || j.employment_type, j.department].filter(Boolean).join(" · ") || "Details to add";
  return (
    <div role="row" className="group grid grid-cols-[minmax(0,1fr)_auto] md:grid-cols-[minmax(0,2.2fr)_minmax(0,1.6fr)_minmax(0,1fr)_minmax(0,1.3fr)_40px] items-center gap-x-6 gap-y-3 px-5 py-4 hover:bg-foreground/[0.025] transition-colors cursor-pointer" onClick={open} data-testid={`job-row-${j.id}`}>
      <div className="min-w-0">
        <div className="flex items-center gap-2"><span className="text-[15px] font-semibold truncate" data-testid={`job-title-${j.id}`}>{j.title === "Untitled role" ? "New role" : j.title}</span>{j.is_sample && <span className="text-[11px] px-1.5 py-0.5 rounded bg-amber-400/12 text-amber-600 dark:text-amber-300 shrink-0">Sample</span>}</div>
        <div className="text-[13px] text-muted-foreground truncate" data-testid={`job-meta-${j.id}`}>{meta}</div>
      </div>
      <div className="col-span-2 md:col-span-1 order-3 md:order-none space-y-1.5 min-w-0" data-testid={`job-lifecycle-${j.id}`}>
        <LifecycleBar step={lc.step} />
        <div className="text-xs text-muted-foreground">{lifecycleCaption(j)}</div>
      </div>
      <div className="hidden md:block text-sm text-foreground/85" data-testid={`job-candidate-count-${j.id}`}>{cands}</div>
      <div className="hidden md:block min-w-0"><NextStep next={lc.next} testId={`job-next-${j.id}`} /></div>
      <div onClick={(e) => e.stopPropagation()} className="justify-self-end">
        <DropdownMenu>
          <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground" aria-label={`Actions for ${j.title}`} data-testid={`job-actions-${j.id}`}><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuItem onClick={() => nav(sectionPath(j.id, "build"))} data-testid={`edit-job-${j.id}`}>Role details</DropdownMenuItem>
            <DropdownMenuItem onClick={() => nav(sectionPath(j.id, "test"))} data-testid={`test-job-${j.id}`}>Assessment</DropdownMenuItem>
            <DropdownMenuItem onClick={() => nav(`/recruiter/pipeline?job=${j.id}`)} data-testid={`pipeline-job-${j.id}`}>Candidate board</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => onDelete(j)} className="text-rose-500 focus:text-rose-500" data-testid={`delete-job-${j.id}`}>Delete role</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}

export default function Jobs() {
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [delTarget, setDelTarget] = useState(null);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [seatsOpen, setSeatsOpen] = useState(false);

  const load = () => {
    setLoading(true); setErr(null);
    api.get("/jobs").then((r) => setJobs(r.data)).catch((e) => setErr(e.message)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const doDelete = async () => {
    if (!delTarget) return;
    try { await api.delete(`/jobs/${delTarget.id}`); toast.success(`Deleted “${delTarget.title}”`); setDelTarget(null); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not delete"); }
  };
  const createJob = async () => {
    try { const { data } = await api.post("/jobs"); nav(`/recruiter/jobs/${data.id}/build`); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not create a role"); }
  };
  useEffect(() => {
    if (params.get("new") === "1") { setParams({}); createJob(); }
    // eslint-disable-next-line
  }, []);

  const counts = useMemo(() => Object.fromEntries(FILTERS.map(([k, , fn]) => [k, jobs.filter(fn).length])), [jobs]);
  const shown = useMemo(() => {
    const fn = FILTERS.find(([k]) => k === filter)[2];
    const t = q.trim().toLowerCase();
    return jobs.filter(fn).filter((j) => !t || [j.title, j.department, j.location].join(" ").toLowerCase().includes(t))
      .sort((a, b) => (b.status === "published") - (a.status === "published") || (b.lifecycle?.step || 0) - (a.lifecycle?.step || 0));
  }, [jobs, filter, q]);

  return (
    <div className="recruiter-page space-y-6" data-testid="jobs-page">
      <header className="flex flex-wrap items-start gap-4" data-testid="jobs-header">
        <div className="flex-1 min-w-[200px]">
          <h1 className="text-3xl sm:text-[32px] font-display font-semibold tracking-tight" data-testid="jobs-title">Jobs</h1>
          <p className="text-[15px] text-muted-foreground mt-1" data-testid="jobs-counts">{jobs.length} role{jobs.length === 1 ? "" : "s"} · {counts.attention || 0} need your attention</p>
        </div>
        <UsageWidget onSeats={() => setSeatsOpen(true)} />
        <Button variant="outline" onClick={() => nav("/recruiter/qbank")} className="gap-2 h-10 font-semibold" data-testid="open-qbank"><Library className="h-4 w-4" /> Question bank</Button>
        <Button onClick={createJob} className="gap-2 h-10 font-semibold" data-testid="create-job-btn"><Plus className="h-4 w-4" /> New job</Button>
      </header>

      {loading ? <RecruiterSkeleton variant="jobs" label="Loading jobs…" /> : err ? <ErrorState message={err} onRetry={load} /> : jobs.length === 0 ? (
        <EmptyState icon={Briefcase} title="No roles yet" description="Describe the role in a sentence and Copilot drafts the spec, the assessment and the careers posting for you to review." action={<Button onClick={createJob} className="mt-2" data-testid="build-first-job">Open your first role</Button>} />
      ) : <>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex flex-wrap gap-2" role="tablist" aria-label="Filter roles">
            {FILTERS.map(([k, label]) => <button key={k} role="tab" aria-selected={filter === k} data-active={filter === k} onClick={() => setFilter(k)} className="hs-pill" data-testid={`jobs-filter-${k}`}>{label} · {counts[k]}</button>)}
          </div>
          <div className="flex-1" />
          <label className="relative w-full sm:w-64">
            <span className="sr-only">Filter jobs</span>
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter jobs" data-testid="jobs-search" className="w-full h-9 pl-9 pr-3 rounded-lg border border-border bg-transparent text-sm focus:outline-none focus:border-primary/50" />
          </label>
        </div>
        <section className="hs-card overflow-hidden" role="table" aria-label="Your roles" data-testid="jobs-list">
          <div role="row" className="hidden md:grid grid-cols-[minmax(0,2.2fr)_minmax(0,1.6fr)_minmax(0,1fr)_minmax(0,1.3fr)_40px] gap-x-6 px-5 py-3 border-b border-border bg-foreground/[0.02]">
            {["Role", "Lifecycle", "Candidates", "Next step", ""].map((h) => <span key={h} role="columnheader" className="hs-th">{h}</span>)}
          </div>
          <div className="divide-y divide-border">
            {shown.map((j) => <JobRow key={j.id} j={j} onDelete={setDelTarget} />)}
            {!shown.length && <p className="px-5 py-10 text-center text-sm text-muted-foreground" data-testid="jobs-no-match">No roles match this filter.</p>}
          </div>
        </section>
      </>}

      <Dialog open={seatsOpen} onOpenChange={setSeatsOpen}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto" data-testid="seats-dialog">
          <DialogHeader><DialogTitle>Private assessment invites</DialogTitle><DialogDescription>One-to-one sign-in links for a cohort, campus drive or referrals.</DialogDescription></DialogHeader>
          <CandidateInvitations />
        </DialogContent>
      </Dialog>
      <Dialog open={!!delTarget} onOpenChange={(v) => !v && setDelTarget(null)}>
        <DialogContent className="max-w-sm" data-testid="delete-job-dialog">
          <DialogHeader><DialogTitle>Delete this role?</DialogTitle><DialogDescription className="sr-only">Confirm permanent deletion of this role and its data.</DialogDescription></DialogHeader>
          <p className="text-sm text-muted-foreground">“{delTarget?.title}” and its candidates, assessments and trials will be permanently removed. This cannot be undone.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelTarget(null)} data-testid="delete-cancel">Cancel</Button>
            <Button variant="destructive" onClick={doDelete} data-testid="delete-confirm">Delete role</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
